/*
 * MyBusinessHome — the company profile. Problems + Builds have their own
 * pages; this page is purely the business picture Orbit collected.
 *
 * Branded per the Agents Anywhere kit:
 *   • IBM Plex Sans (inherited from global font-sans utility)
 *   • Primary blue #18191d (brand-500) for CTAs and active states
 *   • Cool off-white #EEF3FF (brand-50) for soft surfaces / approach pill
 *   • Yellow #FFEA00 (accent-500) for primary callouts when picking actions
 *   • White cards on #F8F9FB app background, rounded-xl, border-gray-200
 *   • Sentence case, periods on every sentence, no emoji
 *
 * Lives inside the global Layout (sidebar + topbar). The page renders into
 * Layout's children outlet — see App.tsx route mount.
 *
 * Data: single round-trip to /api/intake/home/{client_id}. No client-side
 * stitching of legacy business_intelligence + dossier + engagement.
 *
 * Phase 1 shipped here:
 *   ✓ Hero (identity, tier, current approach, switch link)
 *   ✓ Layer progress strip (L1 / L2 / Builds count)
 *   ✓ Tab nav (Overview / Dossier / Scope / Builds / Activity)
 *   ✓ Overview tab — next-best-action, suggestion fallback, lane cards
 *   ✓ Builds tab — projects → "Open workspace" deep-link to validx
 *   ✓ Activity tab — converse_turn_log feed
 *   ✗ Dossier / Scope tabs — wired in next commit (Phase 1.E)
 *   ✗ Right-rail Orbit chat — wired in next commit (Phase 1.D)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOpenWorkspace } from '../hooks/useOpenWorkspace';
import {
  ChevronRight,
  ExternalLink,
  Loader2,
  Sparkles,
  User as UserIcon,
  Zap,
  Users as UsersIcon,
  Compass,
  MessageSquare,
  ShoppingBag,
  FileText,
  Hammer,
  Check,
  X,
  Share2,
  Star,
  Building2,
  Layers,
  Flag,
  Phone,
  Send,
  Plug,
  ArrowRight,
  ArrowUpRight,
} from 'lucide-react';
import clsx from 'clsx';

import { fetchHome, fetchSchema, confirm as confirmField, updateApproach, getBusinessWalk, type HomePayload, type HomeBuild, type WalkState } from '../orbit/lib/intakeApi';
import { requestAgentBridgeUrl } from '../services/api';
import { fetchConnections, type OnboardingConnectionRow } from '../services/onboardingApi';
import ConnectedAppsSection from '../components/onboarding/ConnectedAppsSection';
import AppConnectHub from '../components/onboarding/appConnect/AppConnectHub';
import { APPS_CONNECT_OPEN, openAppConnectHub, type AppsConnectOpenDetail } from '../components/onboarding/appConnect/appConnectEvents';
import { resolveProvider } from '../components/onboarding/providerCatalog';
import { EditableField, STATE_BADGE, fmtValue, toStringArray } from '../components/dossier/fieldKit';
import { AppIcon } from '../components/dossier/AppIcon';
import { GoalsPanel } from '../components/dossier/GoalsPanel';
import type { FieldDef, FieldState, SectionLetter, CompanyContext } from '../orbit/lib/dossier-types';
import {
  fetchStage1Options,
  pickStage1Option,
  type SolutionOptionCard,
  type SolutionOptionKind,
  type Stage1Response,
  type BypassOutcome,
} from '../orbit/lib/stage1Api';
import { pickForwardPath, issueScopeShareToken } from '../orbit/lib/scopeApi';
import { TierPill, type TierSlug } from '../components/billing/TierPill';
import { OrbitChatDrawer } from '../orbit/dossier/OrbitChatDrawer';
// Reuse the same delegation modals the problem detail page uses, so mapping
// the company can be delegated (call / link) per block or for the whole map.
import { ShareModal, CallModal } from './client/ProblemDetail';
import DiscoveryCallModal from '../components/company/DiscoveryCallModal';


// Per AA: two palettes do the work — brand-blue (#18191d) and yellow
// (#FFEA00). No purple/cyan per-lane identities, no left-edge color
// stripes (explicitly avoided). The pill icon does the disambiguation;
// surface stays brand-50 across all three approaches.
const APPROACH_META: Record<string, {
  label: string;
  icon: React.ReactNode;
  pillBg: string;
  pillText: string;
}> = {
  automated: {
    label: 'Automated DIY',
    icon: <Zap size={13} strokeWidth={2.25} />,
    pillBg: 'bg-brand-50',
    pillText: 'text-brand-700',
  },
  paid_human: {
    label: 'Paid Human',
    icon: <UsersIcon size={13} strokeWidth={2.25} />,
    pillBg: 'bg-accent-50',
    pillText: 'text-gray-900',
  },
  explore: {
    label: 'Explore',
    icon: <Compass size={13} strokeWidth={2.25} />,
    pillBg: 'bg-brand-50',
    pillText: 'text-gray-700',
  },
};

type Tab = 'overview' | 'context' | 'scoping';

function readTabFromHash(hash: string): Tab {
  const slug = (hash || '').replace(/^#/, '').toLowerCase();
  if (slug === 'context' || slug === 'dossier' || slug === 'brief') return 'context';
  if (slug === 'scoping' || slug === 'scope') return 'scoping';
  return 'overview';
}


const MyBusinessHome: React.FC<{
  clientId?: string;
  readOnly?: boolean;
  /** B1. Problem scoping is staff-only, so the caller decides. A normal admin
   *  gets the company picture and nothing about how the work is being scoped. */
  showScoping?: boolean;
}> = ({
  clientId: clientIdProp,
  readOnly = false,
  showScoping = false,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mapRequested = searchParams.get('map');
  // When a clientId is passed in (a consultant viewing a specific client's
  // business from the client-detail page), scope to it. The backend
  // /api/intake/home/{client_id} authorizes consultant/super to read any
  // client. Otherwise fall back to the signed-in user's own client.
  const clientId = clientIdProp || localStorage.getItem('client_id') || '';

  const [home, setHome] = useState<HomePayload | null>(null);
  const [fields, setFields] = useState<FieldDef[]>([]);
  const [sectionLabels, setSectionLabels] = useState<Record<string, string>>({});
  const [walk, setWalk] = useState<WalkState | null>(null);
  const [connections, setConnections] = useState<OnboardingConnectionRow[]>([]);
  const [connectHubOpen, setConnectHubOpen] = useState(false);
  const [connectHubPrefill, setConnectHubPrefill] = useState<string | null>(null);
  const [connectHubTab, setConnectHubTab] = useState<'add' | 'connected'>('add');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>(() => readTabFromHash(window.location.hash));

  const loadConnections = useCallback(async () => {
    if (!clientId) return;
    try {
      const rows = await fetchConnections(clientId);
      setConnections(rows);
    } catch (e) {
      console.warn('[my-business] connections fetch failed', e);
    }
  }, [clientId]);

  // Debounced variant for event-driven refetches: a streaming Orbit turn
  // dispatches one orbit:tile-update per intel_update event, and refetching
  // per event hammered /api/onboarding/connections with a burst of identical
  // requests. Trailing 500ms collapses a burst into one fetch.
  const connectionsDebounceRef = React.useRef<number | null>(null);
  const loadConnectionsDebounced = useCallback(() => {
    if (connectionsDebounceRef.current) window.clearTimeout(connectionsDebounceRef.current);
    connectionsDebounceRef.current = window.setTimeout(() => {
      connectionsDebounceRef.current = null;
      void loadConnections();
    }, 500);
  }, [loadConnections]);
  useEffect(() => () => {
    if (connectionsDebounceRef.current) window.clearTimeout(connectionsDebounceRef.current);
  }, []);

  const load = useCallback(async () => {
    if (!clientId) {
      setError('Not signed in.');
      setLoading(false);
      return;
    }
    try {
      const [homeData, schemaData, connRows, walkState] = await Promise.all([
        fetchHome(clientId),
        fetchSchema('all', clientId),
        fetchConnections(clientId).catch(() => []),
        // The walk derives its own position from the dossier, so its `done`
        // flag IS the definition of "business mapped" — no second rulebook.
        // Tolerated failure: a page must never hide itself because a side
        // call fell over.
        getBusinessWalk(clientId).catch(() => null),
      ]);
      setWalk(walkState);
      setHome(homeData);
      setFields(schemaData.fields);
      setSectionLabels(schemaData.sections);
      setConnections(connRows);
      setError(null);
    } catch (e) {
      console.error('[my-business] /home failed', e);
      setError("Couldn't load your business. Try refreshing.");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onTileUpdate = (ev: Event) => {
      // Only connection changes need a connections refetch — every
      // dispatcher sets a section, so a sectionless event is noise, and
      // matching it refetched on EVERY intel update during a chat turn.
      const section = (ev as CustomEvent).detail?.section;
      if (section === 'connections') loadConnectionsDebounced();
    };
    window.addEventListener('orbit:tile-update', onTileUpdate as EventListener);
    return () => window.removeEventListener('orbit:tile-update', onTileUpdate as EventListener);
  }, [loadConnectionsDebounced]);

  useEffect(() => {
    const onConnectOpen = (ev: Event) => {
      const detail = ((ev as CustomEvent).detail || {}) as AppsConnectOpenDetail;
      setConnectHubPrefill(detail.provider || null);
      setConnectHubTab(detail.tab || 'add');
      setConnectHubOpen(true);
    };
    window.addEventListener(APPS_CONNECT_OPEN, onConnectOpen as EventListener);
    return () => window.removeEventListener(APPS_CONNECT_OPEN, onConnectOpen as EventListener);
  }, []);

  useEffect(() => {
    const onHash = () => setTab(readTabFromHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const onTabChange = useCallback((next: Tab) => {
    setTab(next);
    window.location.hash = next === 'overview' ? '' : `#${next}`;
  }, []);

  const openWorkspace = useCallback(async (build: HomeBuild) => {
    // Same pattern /agents → New agent and ClientAgentsTab use: ask the
    // backend for an SSO-bridged validx URL so the user lands inside
    // /customer/project/<pid>/builder/ already authenticated. Hitting
    // the validx URL directly drops the session and dumps the user back
    // on the validx sign-in page.
    if (!build.project_id) return;
    const clientId = home?.client.id || '';
    try {
      const redirect = `/customer/project/${build.project_id}/builder/?clientId=${clientId}`;
      const data = await requestAgentBridgeUrl(redirect, clientId);
      if (data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      console.error('[my-business] bridge URL request failed', e);
    }
  }, [home]);

  const suggestedConnectSlugs = useMemo(() => {
    const cc: any = home?.company_context || {};
    const raw = cc.section_b?.tools_used;
    const tools = Array.isArray(raw)
      ? raw.map(String)
      : (typeof raw === 'string' && raw.trim() ? raw.split(',').map((s: string) => s.trim()) : []);
    return tools.map(t => resolveProvider(t).provider.slug);
  }, [home?.company_context]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-3 text-gray-400 font-sans">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Loading your business…</span>
      </div>
    );
  }
  if (error || !home) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 font-sans">
        <p className="text-gray-700">{error || 'Something went wrong.'}</p>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1 rounded-md bg-black px-4 py-2 text-sm text-white hover:bg-brand-500 whitespace-nowrap"
        >
          Retry
        </button>
      </div>
    );
  }

  const approach = home.engagement?.selected_approach || null;
  const approachMeta = approach ? APPROACH_META[approach] : null;
  const nba = home.route_plan?.next_best_action ?? null;

  // A profile of "Ask Orbit" rows is not a business picture — it is a page
  // asking the owner to do the thing they have not been asked to do yet. Until
  // the business is mapped OR a problem is scoped, this page IS the onboarding
  // prompt. A consultant viewing read-only is exempt: they see whatever exists,
  // and the drawer that would drive the walk is not mounted for them anyway.
  const problemsScoped = (home.problems_summary?.scoped ?? 0) > 0;
  // `?map=1` is the onboarding sending someone here BECAUSE answers are still
  // missing. Honour it over the scoped-problem exemption: a company that scoped
  // a problem early would otherwise be shown the half-empty profile it was just
  // redirected away from.
  const askedForMapping = mapRequested === '1';
  const needsOnboarding = !readOnly && !!walk && !walk.done && (askedForMapping || !problemsScoped);

  return (
    <div className="font-sans text-gray-900 space-y-6">
      {needsOnboarding && (
        <OnboardingGate walk={walk} clientId={home.client.id} />
      )}

      {!needsOnboarding && (
        <>
          <ProfileBanner home={home} approachMeta={approachMeta} fields={fields} />

          {/* Company profile — the business picture Orbit has collected. Problems
              and Builds live on their own pages now; this page is purely "who you
              are." Completion is COMPANY-based (A·B·C·D·F), never problem-based. */}
          <main className="space-y-8">
            <CompanyProfile
              home={home}
              fields={fields}
              clientId={home.client.id}
              connections={connections}
              onConnectionsChange={loadConnections}
            />
            {/* B4 — every context field, editable, saving as you type. The
                blocks above are a curated summary; this is where someone who
                knows the answer fixes it without waiting for Orbit to ask.
                Folded away by default so the summary stays the page. */}
            {!readOnly && (
              <AllFieldsDisclosure
                fields={fields}
                sectionLabels={sectionLabels}
                companyContext={home.company_context}
                clientId={home.client.id}
                onCommitted={load}
              />
            )}
            {showScoping && (
              <ScopingTab
                fields={fields}
                sectionLabels={sectionLabels}
                companyContext={home.company_context}
                clientId={home.client.id}
                engagementId={home.engagement?.id || null}
                onCommitted={load}
                available={!!home.engagement?.selected_approach}
              />
            )}
          </main>
        </>
      )}

      {/* Persistent Orbit chat — launcher + drawer. Refreshes home after
          each turn so the layer strip + lane card update in real time.
          Suppressed in read-only mode (consultant viewing a client): the
          live dock's OrbitIntake is bound to the signed-in user's
          localStorage client_id, so it can't safely drive another client's
          session here — the consultant uses the Orbit-link / Impersonate
          actions instead. */}
      {!readOnly && (
        <OrbitChatDrawer
          clientId={home.client.id}
          onAfterTurn={() => { load(); loadConnections(); }}
          initialActiveField={home.company_context.last_inquired_field}
          // Tab declares the Inquirer scope. Scoping tab → L2 only (never
          // serve L1 leftovers like primary_comms here). Company Context
          // tab → L1 only (L1 dossier surface). Overview defaults to the
          // unfinished layer: L1 if brief is incomplete, else L2.
          layer="L1"
          route="company"
          context="company"
          // No persistent bottom "Talk to Orbit" launcher on My Business — the
          // page's inline "Ask Orbit →" CTAs summon the dock via orbit:open.
          initialGreeting={
            home.layers.l1.blocking_remaining > 0
              ? `Let's round out your company picture — ${home.layers.l1.blocking_remaining} to go. Or tell me a problem anytime.`
              : 'Tell me anything about your business and I will keep your profile current.'
          }
        />
      )}

      <AppConnectHub
        open={connectHubOpen}
        onClose={() => {
          setConnectHubOpen(false);
          setConnectHubPrefill(null);
        }}
        clientId={home.client.id}
        connections={connections}
        suggestedSlugs={suggestedConnectSlugs}
        onConnected={loadConnections}
        initialProvider={connectHubPrefill}
        initialTab={connectHubTab}
      />
    </div>
  );
};

// ── Company profile — the business picture, organized into blocks ──

const fmtVal = (v: any): string => Array.isArray(v) ? v.filter(Boolean).join(', ') : String(v ?? '');
const isFilled = (v: any): boolean => Array.isArray(v) ? v.length > 0 : (v != null && String(v).trim() !== '');

// App value → Simple Icons slug. Real brand logos via cdn.simpleicons.org —
// no asset hosting, no backend. Covers the b.tools_used registry values plus
// common stack tools a website scan / free-text can surface. Unknown → monogram.
const CompanyProfile: React.FC<{
  home: HomePayload;
  fields: FieldDef[];
  clientId: string;
  connections: OnboardingConnectionRow[];
  onConnectionsChange: () => void;
}> = ({ home, fields, clientId, connections, onConnectionsChange }) => {
  const cc: any = home.company_context || {};
  const get = (sec: string, sub: string) => (cc['section_' + sec] || {})[sub];

  // Delegation — get a teammate to help map the business (call or link),
  // scoped to one block or the whole profile (A/B/C/D). Reuses the same
  // modals the problem detail page uses.
  const engagementId = home.engagement?.id || null;
  // B2. Delegation used to be a banner over the whole page, which invited
  // handing off the entire profile. You delegate a gap, not a page: the target
  // is one block, chosen by clicking the block that is short of answers.
  const [delegate, setDelegate] = useState<{ letter: string; how: 'link' | 'call' } | null>(null);
  const COMPANY_BLOCKS = [
    { letter: 'a', label: 'Company' },
    { letter: 'b', label: 'Tools & stack' },
    { letter: 'c', label: 'Teams' },
    { letter: 'd', label: 'Goals' },
  ];
  const blockLabel = (letter: string) =>
    COMPANY_BLOCKS.find(b => b.letter === letter)?.label || `Section ${letter.toUpperCase()}`;

  // value → human label for select/multiselect (registry options)
  const optMap: Record<string, Record<string, string>> = {};
  for (const f of fields) {
    if (f.options && f.options.length) {
      optMap[f.key] = Object.fromEntries(f.options.map((o: any) => [o.value, o.label]));
    }
  }
  const label = (sec: string, sub: string): string => {
    const v = get(sec, sub);
    const m = optMap[`${sec}.${sub}`];
    if (!m) return fmtVal(v);
    if (Array.isArray(v)) return v.map(x => m[x] || x).join(', ');
    return m[v] || fmtVal(v);
  };

  // Company-only completion (A·B·C·D·F) — never problem-based.
  const COMPANY_SECTIONS = ['a', 'b', 'c', 'd', 'f'];
  const reqFields = fields.filter(f => COMPANY_SECTIONS.includes((f.key || '').split('.')[0]) && (f.required_for_routing || f.critical));
  const filled = reqFields.filter(f => { const [s, sub] = f.key.split('.'); return isFilled(get(s, sub)); });
  const pct = reqFields.length ? Math.round(100 * filled.length / reqFields.length) : 0;
  const missing = reqFields.filter(f => { const [s, sub] = f.key.split('.'); return !isFilled(get(s, sub)); }).slice(0, 3);

  const tools = toStringArray(get('b', 'tools_used'));
  const connectedSlugs = useMemo(() => new Set(
    (connections || [])
      .filter(c => {
        const s = String(c.status || '').toLowerCase();
        return s === 'connected' || s === 'active';
      })
      .map(c => c.provider.toLowerCase()),
  ), [connections]);
  const departments = toStringArray(get('c', 'departments'));
  const deptCount = get('c', 'department_count');
  // (Brand logo + colors moved to the header / ProfileBanner — see below.)
  const summaryBits = [label('a', 'industry'), label('a', 'org_type'), label('a', 'stage'), label('a', 'hq_country')].filter(Boolean);

  // Per-area completion — counts the REAL registry fields in each mapping
  // section (not a hand-picked subset), so the badge + header reflect every
  // field Orbit actually maps and the number MOVES when you answer one.
  // Mirrors the Orbit intake dossier counter (same a·b·c·d scope, same
  // industry_detail exclusion) so both surfaces always agree.
  //
  // Company MAPPING = A·B·C·D only. Budget (F) and Engagement (G) are the
  // commercial / decision layer — they qualify a deal, not the company — so
  // they are NOT counted here (and F is no longer shown as a profile block).
  const MAPPING_SECTIONS = ['a', 'b', 'c', 'd'];
  const blockStat = (sec: string) => {
    // `hidden` means this company will never be asked the question: its
    // visibility rule does not pass for them (data residency is only asked at
    // 51+ headcount, investment detail only if the outcome was mixed or
    // wasted). Counting those made every block unreachable for a small
    // company, and left the footer reporting a gap against a row that is not
    // even drawn. The backend evaluates the rules against this company's own
    // dossier and ships the answer, so this is a read, not a second rulebook.
    const secFields = fields.filter(
      f => (f.key || '').split('.')[0] === sec
        && !f.hidden
        && f.key !== 'a.industry_detail',
    );
    const filledN = secFields.filter(f => isFilled(get(sec, f.key.split('.')[1]))).length;
    const total = secFields.length;
    return { total, filled: filledN, pct: total ? Math.round((100 * filledN) / total) : 0 };
  };
  const profileFilled = MAPPING_SECTIONS.reduce((n, sec) => n + blockStat(sec).filled, 0);
  const profileTotal = MAPPING_SECTIONS.reduce((n, sec) => n + blockStat(sec).total, 0);

  const Row: React.FC<{ sec: string; sub: string; k: string }> = ({ sec, sub, k }) => {
    const v = get(sec, sub);
    const has = isFilled(v);
    return (
      <div className="flex items-baseline gap-3 py-1.5 border-t border-brand-100 first:border-t-0 text-[13px]">
        <span className="w-[120px] shrink-0 text-gray-500 text-[12.5px]">{k}</span>
        <span className={clsx('font-medium', has ? 'text-gray-900' : 'text-gray-400')}>{has ? label(sec, sub) : 'Ask Orbit'}</span>
        <span className={clsx('ml-auto text-[13px]', has ? 'text-brand-500' : 'text-gray-300')}>{has ? '●' : '○'}</span>
      </div>
    );
  };
  const Block: React.FC<{ icon: any; title: string; sec: string; children: React.ReactNode }> = ({ icon: Icon, title, sec, children }) => {
    const st = blockStat(sec);
    const done = st.total > 0 && st.filled >= st.total;
    return (
      <section className="bg-brand-50 rounded-2xl p-5 ring-1 ring-gray-200/60">
        <div className="flex items-center gap-2.5 mb-3">
          <div className={clsx('w-8 h-8 rounded-lg grid place-items-center shrink-0', done ? 'bg-green-100 text-green-700' : 'bg-brand-100 text-brand-600')}>
            {done ? <Check size={16} strokeWidth={2.25} /> : <Icon size={16} strokeWidth={2} />}
          </div>
          <span className="font-semibold text-[14.5px] text-gray-900">{title}</span>
          <span className="ml-auto text-[10.5px] font-sans uppercase tracking-wider text-gray-400 tabular-nums shrink-0">{st.filled}/{st.total}</span>
        </div>
        {/* Per-area progress — mirrors the dimension bars on the problem detail page. */}
        <div className="h-1.5 bg-white rounded-full overflow-hidden mb-4">
          <div className="h-full bg-brand-500 rounded-full transition-[width] duration-500" style={{ width: `${st.pct}%` }} />
        </div>
        {children}
        {/* A6 — fields this company added itself. The rows above are a curated
            list with labels written into the JSX, so an added field would
            otherwise be invisible here while still counting toward the badge
            above: the count reads 9/10 and only nine rows exist. Added fields
            append after the curated ones, in registry order, exactly as they
            do on the onboarding cards. */}
        {fields
          .filter(f => f.added && !f.hidden && (f.key || '').split('.')[0] === sec)
          .map(f => (
            <Row key={f.key} sec={sec} sub={f.key.split('.').slice(1).join('.')} k={f.label} />
          ))}
        {/* B2 — delegation lives on the gap. Only offered while this block is
            short of answers: a finished block has nothing to hand off. Neither
            action needs an engagement any more, which matters because most
            companies do not have one and were being offered neither. */}
        {!done && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-brand-100 pt-3">
            <span className="text-[11.5px] text-gray-500 mr-auto">
              {st.total - st.filled} unanswered here.
            </span>
            <button
              type="button"
              onClick={() => setDelegate({ letter: sec, how: 'link' })}
              className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[11.5px] font-semibold text-gray-700 ring-1 ring-gray-200 transition-colors hover:ring-brand-300 whitespace-nowrap"
            >
              <Send className="w-3 h-3" /> Send a link
            </button>
            <button
              type="button"
              onClick={() => setDelegate({ letter: sec, how: 'call' })}
              className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[11.5px] font-semibold text-gray-700 ring-1 ring-gray-200 transition-colors hover:ring-brand-300 whitespace-nowrap"
            >
              <Phone className="w-3 h-3" /> Ask Orbit to call
            </button>
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="space-y-5">
      {/* Section heading — frames the area cards, mirrors the "Scoping summary"
          heading + captured count on the problem detail page. */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-[17px] font-semibold text-gray-900 tracking-tight">Company profile</h2>
          <p className="text-[12.5px] text-gray-400 mt-0.5">The business picture Orbit has mapped, organized by area.</p>
        </div>
        <span className="text-[11px] font-sans uppercase tracking-wider text-gray-400 tabular-nums shrink-0">{profileFilled} / {profileTotal} captured</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {/* The completion + summary block moved UP into ProfileBanner (one
          merged block, professional copy). The detail blocks remain. */}
      <Block icon={Building2} title="Company" sec="a">
        <Row sec="a" sub="company_name" k="Name" />
        <Row sec="a" sub="website" k="Website" />
        <Row sec="a" sub="industry" k="Industry" />
        <Row sec="a" sub="org_type" k="Type" />
        <Row sec="a" sub="stage" k="Stage" />
        <Row sec="a" sub="hq_country" k="HQ" />
        <Row sec="a" sub="regions" k="Operates in" />
        <Row sec="a" sub="headcount" k="Headcount" />
      </Block>

      <Block icon={Layers} title="Tools & stack" sec="b">
        {tools.length > 0 ? (
          <div className="mb-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">From your profile</div>
            <div className="flex flex-wrap gap-2">
              {tools.map(t => {
                const lbl = (optMap['b.tools_used'] || {})[t] || t;
                const { provider } = resolveProvider(t);
                const connected = connectedSlugs.has(provider.slug) || connectedSlugs.has(String(t).toLowerCase());
                return (
                  <button
                    key={t}
                    type="button"
                    disabled={connected}
                    onClick={() => openAppConnectHub({ provider: provider.slug, tab: 'add' })}
                    className={clsx(
                      'inline-flex items-center gap-2 bg-white rounded-xl pl-2 pr-2.5 py-1.5 ring-1 text-left transition-colors',
                      connected ? 'ring-gray-200 cursor-default' : 'ring-amber-200 hover:ring-brand-300 hover:bg-brand-50/40',
                    )}
                  >
                    <AppIcon value={t} label={lbl} />
                    <span className="text-[12.5px] font-medium text-gray-800">{lbl}</span>
                    <span className={clsx('text-[10px] font-semibold', connected ? 'text-green-600' : 'text-amber-600')}>
                      {connected ? 'Connected' : 'Connect'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-[12.5px] text-gray-500 mb-3">Connect the apps your team uses so Orbit can work with your stack.</p>
        )}
        <button
          type="button"
          onClick={() => openAppConnectHub({ tab: 'add' })}
          className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 mb-3 text-[13px] font-semibold text-white transition-colors hover:brightness-105 whitespace-nowrap"
          style={{ background: '#18191d', boxShadow: '0 2px 8px rgba(24,25,29,0.28)' }}
        >
          <Plug size={14} strokeWidth={2.25} /> Connect an app
        </button>
        <Row sec="b" sub="cloud" k="Cloud" />
        <Row sec="b" sub="primary_crm" k="CRM" />
        <Row sec="b" sub="primary_comms" k="Comms" />
      </Block>

      <Block icon={UsersIcon} title="Teams" sec="c">
        {departments.length > 0 ? (
          <>
            <div className="flex flex-wrap gap-2 mb-2">
              {departments.map(d => {
                const lbl = (optMap['c.departments'] || {})[d] || d;
                return <span key={d} className="bg-white rounded-full px-3 py-1 text-[12px] font-medium text-gray-800 ring-1 ring-gray-200">{lbl}</span>;
              })}
            </div>
            <div className="text-xs text-gray-500 mt-1">{deptCount ? `${deptCount} departments. ` : ''}Assign members to departments on the Team page.</div>
          </>
        ) : (
          <Row sec="c" sub="departments" k="Departments" />
        )}
      </Block>

      {/* Budget (F) intentionally NOT shown here — it's the commercial /
          decision layer (qualifies a deal), surfaced per-problem at the
          routing step, not part of mapping the business. */}

      <Block icon={Flag} title="Goals & posture" sec="d">
        <Row sec="d" sub="ai_motivation" k="Why AI now" />
        <Row sec="d" sub="growth_priorities" k="Priorities" />
        <Row sec="d" sub="adoption_posture" k="AI posture" />
        <Row sec="d" sub="ai_adoption_stage" k="Where you are" />
        {/* A5 — the goals themselves, which the four rows above cannot hold:
            they are single answers and all framed around AI. Same component the
            onboarding goals card uses, so both surfaces stay in step. */}
        <div className="mt-4 border-t border-brand-100 pt-3">
          <GoalsPanel clientId={clientId} />
        </div>
      </Block>
      </div>

      <ConnectedAppsSection
        connections={connections}
        clientId={clientId}
        onConnected={() => onConnectionsChange()}
      />

      {delegate && delegate.how === 'link' && (
        <ShareModal
          engagementId={engagementId}
          clientId={home.client.id}
          letter={delegate.letter}
          role="teammate"
          layer="L1"
          blocks={[{ letter: delegate.letter, label: blockLabel(delegate.letter) }]}
          sectionLabel={blockLabel(delegate.letter)}
          onClose={() => setDelegate(null)}
        />
      )}
      {delegate && !engagementId && delegate.how === 'call' && (
        <DiscoveryCallModal
          clientId={home.client.id}
          clientName={home.client.name || 'this company'}
          sectionLabel={blockLabel(delegate.letter)}
          defaultPhone={(home.client as { phone?: string }).phone || ''}
          onClose={() => setDelegate(null)}
        />
      )}
      {delegate && engagementId && delegate.how === 'call' && (
        <CallModal
          engagementId={engagementId}
          problemId={undefined}
          clientId={home.client.id}
          letter={delegate.letter}
          role="teammate"
          layer="L1"
          blocks={[{ letter: delegate.letter, label: blockLabel(delegate.letter) }]}
          sectionLabel={blockLabel(delegate.letter)}
          onClose={() => setDelegate(null)}
        />
      )}
    </div>
  );
};

export default MyBusinessHome;


// ── Profile banner ────────────────────────────────────────────────────
//
// Mirrors the consultant ClientDetail header (avatar tile, name + status
// pill, email | website, right-side chips) and merges in the profile
// completeness roll-up that used to be a separate LayerStrip below. One
// banner = single source of identity + readiness for the page.

interface ProfileBannerProps {
  home: HomePayload;
  approachMeta: typeof APPROACH_META[keyof typeof APPROACH_META] | null;
  fields: FieldDef[];
}

interface BannerStatus {
  label: string;
  dotClass: string;
  pillClass: string;
}

function computeBannerStatus(home: HomePayload): BannerStatus {
  const briefIncomplete = home.layers.l1.blocking_remaining > 0;
  const approach = home.engagement?.selected_approach || null;
  const hasLiveBuild = home.builds.some(b => !!b.workspace_url);
  if (hasLiveBuild) {
    return {
      label: 'Building',
      dotClass: 'bg-accent-500',
      pillClass: 'bg-accent-50 text-gray-900 ring-accent-200',
    };
  }
  if (briefIncomplete) {
    return {
      label: 'Brief in progress',
      dotClass: 'bg-gray-400',
      pillClass: 'bg-gray-50 text-gray-600 ring-gray-200',
    };
  }
  if (!approach) {
    return {
      label: 'Routing locked',
      dotClass: 'bg-brand-500',
      pillClass: 'bg-brand-50 text-brand-700 ring-brand-200',
    };
  }
  return {
    label: 'Scoping in progress',
    dotClass: 'bg-brand-500',
    pillClass: 'bg-brand-50 text-brand-700 ring-brand-200',
  };
}

/* ── Onboarding gate ────────────────────────────────────────────────────
   Shown INSTEAD of the company profile until the business is mapped or a
   problem is scoped. The profile grid is honest but useless at that point:
   two rows of values and twenty-six of "Ask Orbit" reads as a broken page
   rather than an invitation, and it buries the one action that matters. */
const OnboardingGate: React.FC<{
  walk: WalkState;
  clientId: string;
}> = ({ walk, clientId }) => {
  const { opening, open: openWorkspace } = useOpenWorkspace();
  const answered = walk.progress?.answered ?? 0;
  const total = walk.progress?.total ?? 0;
  const started = answered > 0;

  let heading = "Let's map your business";
  if (started) heading = 'Finish mapping your business';

  let blurb = "Orbit asks about your company, your stack, how you're organised and what "
    + 'you\u2019re aiming at \u2014 then this page fills itself in. Give it your website and it '
    + 'reads that first, so most of the rest is just confirming.';
  if (started) {
    blurb = 'You are part way through. Pick up where you left off and this page fills '
      + 'itself in as you go.';
  }

  let cta = 'Start mapping';
  if (started) cta = 'Continue mapping';

  // Scoping happens in the builder workspace, so this crosses rather than
  // navigating — and says so, because it opens a new tab.
  let problemCta = 'Scope a problem in the workspace';
  if (opening) problemCta = 'Opening…';

  const openWalk = () => {
    window.dispatchEvent(new CustomEvent('orbit:open', {
      detail: { route: 'company', fresh: false, context: 'company' },
    }));
  };

  let pct = 0;
  if (total > 0) pct = Math.round((answered / total) * 100);

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-8 sm:p-10">
      <div className="max-w-xl">
        <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.1em] text-brand-600">
          Onboarding
        </span>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-gray-900">{heading}</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-gray-600">{blurb}</p>

        {started && (
          <div className="mt-6">
            <div className="flex items-baseline justify-between">
              <span className="font-sans text-[11px] uppercase tracking-wider text-gray-400">
                Progress
              </span>
              <span className="font-sans text-[12px] tabular-nums text-gray-500">
                {answered} of {total} answered
              </span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        <div className="mt-7 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={openWalk}
            className="inline-flex items-center gap-2 rounded-full bg-brand-500 px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-brand-600 whitespace-nowrap"
          >
            {cta}
            <ArrowRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => openWorkspace(clientId)}
            disabled={opening}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-[14px] font-semibold text-gray-700 transition-colors hover:border-brand-300 disabled:opacity-60 whitespace-nowrap"
          >
            {problemCta}
            <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-5 text-[13px] text-gray-400">
          Either one unlocks this page — map the business here, or scope a problem in
          the workspace.
        </p>
      </div>
    </div>
  );
};


const ProfileBanner: React.FC<ProfileBannerProps> = ({ home, approachMeta, fields }) => {
  const { client, layers } = home;
  const status = computeBannerStatus(home);

  // Professional profile summary (replaces the old field-tally captions —
  // field counts stay behind the scenes as Orbit's concern). A human
  // descriptor line + the next genuine gap, mirroring the old company card.
  const cc: any = home.company_context || {};
  const getCC = (sec: string, sub: string) => (cc['section_' + sec] || {})[sub];
  const optMapB: Record<string, Record<string, string>> = {};
  for (const f of fields) {
    if (f.options && f.options.length) {
      optMapB[f.key] = Object.fromEntries(f.options.map((o: any) => [o.value, o.label]));
    }
  }
  const labelCC = (sec: string, sub: string): string => {
    const v = getCC(sec, sub);
    const m = optMapB[`${sec}.${sub}`];
    if (!m) return fmtVal(v);
    if (Array.isArray(v)) return v.map(x => m[x] || x).join(', ');
    return m[v] || fmtVal(v);
  };
  const COMPANY_SECTIONS = ['a', 'b', 'c', 'd', 'f'];
  const reqFields = fields.filter(f => COMPANY_SECTIONS.includes((f.key || '').split('.')[0]) && (f.required_for_routing || f.critical));
  const missing = reqFields.filter(f => { const [s, sub] = f.key.split('.'); return !isFilled(getCC(s, sub)); }).slice(0, 3);
  const summaryBits = [labelCC('a', 'industry'), labelCC('a', 'org_type'), labelCC('a', 'stage'), labelCC('a', 'hq_country')].filter(Boolean);
  const websiteShort = (client.website_url || '')
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '');
  const initial = (client.name || client.email || '?').charAt(0).toUpperCase();
  // Branding extracted from the website read — shown in the header (logo in
  // the avatar, color swatches under the name) so the business sees its own
  // identity, framed as "we pulled this from your site."
  const brandLogo = (client.branding?.logo || client.branding?.images?.logo || '').trim() || null;
  const _bc = client.branding?.colors || {};
  const brandColors = [_bc.primary, _bc.secondary, _bc.accent]
    .map(c => (c || '').trim())
    .filter(Boolean);

  // Combined profile completeness — single roll-up across L1 + L2 visible
  // fields. Per-layer bars below give the breakdown without competing for
  // headline weight.
  const totalAll = layers.l1.total + layers.l2.total;
  const capturedAll = layers.l1.captured + layers.l2.captured;
  const overallPct = totalAll > 0 ? Math.round((capturedAll / totalAll) * 100) : 0;
  const l1Pct = layers.l1.total > 0
    ? Math.round((layers.l1.captured / layers.l1.total) * 100) : 0;
  const l2Pct = layers.l2.total > 0
    ? Math.round((layers.l2.captured / layers.l2.total) * 100) : 0;

  return (
    <header className="relative overflow-hidden rounded-3xl bg-white px-6 py-6 ring-1 ring-gray-200/70 shadow-sm">
      {/* Single subtle hero radial — the only gradient the brand allows. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(24,25,29,0.10), transparent 70%)' }}
      />
      {/* Identity row — avatar, name + status pill, right-side chips */}
      <div className="relative flex items-start justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="relative grid h-14 w-14 place-items-center rounded-2xl bg-brand-50 text-brand-600 text-xl font-bold shrink-0 overflow-hidden ring-1 ring-gray-200/60">
            <span>{initial}</span>
            {brandLogo && (
              <img
                src={brandLogo}
                alt=""
                className="absolute inset-0 h-full w-full object-contain bg-white p-1.5"
                onError={(e) => { (e.currentTarget as HTMLImageElement).remove(); }}
              />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 truncate">
                {client.name || 'Your business'}
              </h1>
              <span
                className={clsx(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset',
                  status.pillClass,
                )}
              >
                <span className={clsx('w-1.5 h-1.5 rounded-full', status.dotClass)} />
                {status.label}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-3 text-sm text-gray-500 flex-wrap">
              {client.email && <span className="truncate">{client.email}</span>}
              {client.email && websiteShort && (
                <span className="text-gray-300">|</span>
              )}
              {websiteShort && (
                <a
                  href={(client.website_url || '').startsWith('http')
                    ? client.website_url!
                    : `https://${client.website_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 transition-colors"
                >
                  {websiteShort}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            {/* Brand — extracted from the site. Swatches + framing when we
                have it; a soft prompt when we don't. */}
            <div className="mt-2 flex items-center gap-2">
              {brandColors.length > 0 ? (
                <>
                  <span className="text-[11px] uppercase tracking-wider font-sans text-gray-400">Brand</span>
                  <div className="flex items-center gap-1">
                    {brandColors.map(c => (
                      <span key={c} title={c} className="w-4 h-4 rounded ring-1 ring-black/5" style={{ background: c }} />
                    ))}
                  </div>
                  <span className="text-[11px] text-gray-400">extracted from your site</span>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event('orbit:open'))}
                  className="text-[11px] text-gray-400 hover:text-brand-600 transition-colors"
                >
                  <span className="text-gray-300">+</span> Add your brand — we read your logo &amp; colors from your site
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <TierPill tier={client.tier as TierSlug} />
          {approachMeta && (
            <span
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium',
                approachMeta.pillBg, approachMeta.pillText,
              )}
            >
              {approachMeta.icon}
              <span>{approachMeta.label}</span>
            </span>
          )}
        </div>
      </div>

      {/* Completeness roll-up — the primary ring on the left, a professional
          profile summary on the right. The ring carries the percentage; the
          text reads like a human profile (who they are + the next gap),
          never raw field counts. */}
      <div className="relative mt-5 border-t border-gray-100 pt-5">
        <div className="flex items-center gap-4">
          <ProfilePrimaryRing size={96} stroke={9} pct={overallPct} />
          <div className="flex flex-col leading-tight gap-1 min-w-0">
            <span className="text-[10px] font-sans uppercase tracking-wider text-gray-400">Profile completeness</span>
            <span className="text-[15px] text-gray-900">
              {summaryBits.length ? summaryBits.join(' · ') : 'Tell Orbit about your business to fill this in.'}
            </span>
            {missing.length > 0 && (
              <span className="text-xs text-gray-500 mt-0.5">
                Still missing: {missing.map(f => f.label).join(', ')}.
              </span>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// Just the ring, no outboard label/detail — the banner renders its own
// caption stack to the right so Brief and Scoping sit as sibling captions
// next to the "Profile completeness · 58/76 fields" headline.
const ProfilePrimaryRing: React.FC<{ size: number; stroke: number; pct: number }> = ({ size, stroke, pct }) => {
  const safePct = Math.max(0, Math.min(100, pct));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (safePct / 100) * circumference;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} role="img" aria-label={`Profile ${safePct}%`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="#F1F2F4" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          stroke="#FFEA00" strokeWidth={stroke}
          strokeLinecap="round" fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 400ms ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xl font-semibold text-gray-900 tabular-nums leading-none">
          {safePct}%
        </span>
      </div>
    </div>
  );
};

// ── Tabs ──────────────────────────────────────────────────────────────

const TabBtn: React.FC<{
  label: string;
  value: Tab;
  active: Tab;
  onPick: (t: Tab) => void;
  badge?: number;
}> = ({ label, value, active, onPick, badge }) => {
  const isOn = active === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isOn}
      onClick={() => onPick(value)}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-sm font-medium transition-colors whitespace-nowrap',
        isOn
          ? 'bg-gray-900 text-white hover:bg-gray-800'
          : 'text-gray-600 hover:bg-gray-50',
      )}
    >
      <span>{label}</span>
      {badge != null && badge > 0 && (
        <span className={clsx(
          'inline-flex h-4 min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-semibold',
          isOn ? 'bg-accent-500 text-gray-900' : 'bg-brand-500 text-white',
        )}>
          {badge}
        </span>
      )}
    </button>
  );
};


// ── Overview tab ──────────────────────────────────────────────────────

interface OverviewTabProps {
  home: HomePayload;
  onApproachPicked: () => void;
  onContinue: () => void;
  onOpenWorkspace: (build: HomeBuild) => void | Promise<void>;
  /** Refetches the home payload. Same shape as onApproachPicked (both
   *  resolve to the parent's `load`) but named generically so child
   *  flows (forward-path pick, etc.) read more naturally. */
  onReload: () => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ home, onApproachPicked, onContinue, onOpenWorkspace, onReload }) => {
  const nba = home.route_plan?.next_best_action ?? null;
  const approach = home.engagement?.selected_approach || null;
  const briefIncomplete = home.layers.l1.blocking_remaining > 0;
  // Lock the approach strip once the engagement has reached the
  // Experience Report milestone (≥1 problem scoped) — switching paths
  // there would discard the scoping work.
  const problemsSummary = home.problems_summary || { total: 0, scoped: 0 };
  const approachLocked = problemsSummary.scoped > 0;

  // When the user still owes brief answers, route them back to Orbit chat
  // (or /orbit/intake if the chat drawer can't open from here).
  const openBriefChat = useCallback(() => {
    // Fire orbit:route BEFORE opening the drawer so the context binds 
    // strictly as a Company mapping turn.
    window.dispatchEvent(new CustomEvent('orbit:route', { detail: { route: 'company' } }));
    window.dispatchEvent(new CustomEvent('orbit:open', { detail: { route: 'company', fresh: false, context: 'company' } }));
  }, []);

  // Card hierarchy on Overview:
  //   1) Brief still incomplete → "Finish your brief" is the only CTA.
  //   2) Brief done, no approach → Approach strip is the action surface.
  //   3) Brief done, approach picked → Lane card owns the next step.
  //
  // The nba next-best-action is a global router signal. On /my-business
  // the lane card always carries the same intent in phase-aware copy,
  // so we never render nba alongside an active lane card.
  const showLane = !briefIncomplete && !!approach;
  const showStrip = !briefIncomplete;

  // v0.5 — Drop the ApproachStrip + lane-card screen entirely. The new
  // flow: Orbit lives in the dock (already mounted at the page level
  // via OrbitChatDrawer). My Business shows:
  //   • Brief-incomplete prompt (when applicable) — opens Orbit dock
  //   • Builds list — one card per scoped problem with status +
  //     "Open workspace" CTA
  //   • Profile completeness — slim card with the captured/total ring
  //     and a "Tell Orbit more" CTA that opens the dock
  // The old Automated / Paid Human / Explore lane cards are retired —
  // Orbit decides those paths in chat now, no separate top-level menu.
  const builds = home.builds || [];
  const layers = home.layers;
  const overallCaptured = layers.l1.captured + layers.l2.captured;
  const overallTotal = layers.l1.total + layers.l2.total;

  return (
    <div className="flex flex-col gap-3">
      {briefIncomplete && (
        <Card primary>
          <CardHead
            icon={<Sparkles size={15} strokeWidth={2} />}
            title="Pick up where you left off."
          />
          <p className="text-sm leading-relaxed text-gray-600">
            Orbit still has a few questions to round out your picture — quick ones, no long forms.
          </p>
          <CardCTA
            onClick={openBriefChat}
            label="Open Orbit"
          />
        </Card>
      )}

      <BuildsListCard
        builds={builds}
        onOpenWorkspace={onOpenWorkspace}
      />

      <ProfileCompletenessCard
        captured={overallCaptured}
        total={overallTotal}
        onTellOrbit={openBriefChat}
      />
    </div>
  );
};


// ── v0.5 — Builds list ───────────────────────────────────────────────
//
// One card per validx project this client owns. Each carries the
// project title + status pill + "Open workspace" CTA that bridges
// into validx via the existing SSO bridge URL.
const BuildsListCard: React.FC<{
  builds: HomeBuild[];
  onOpenWorkspace: (build: HomeBuild) => void | Promise<void>;
}> = ({ builds, onOpenWorkspace }) => {
  if (builds.length === 0) {
    return (
      <Card>
        <CardHead
          icon={<Hammer size={15} strokeWidth={2} />}
          title="No builds yet."
        />
        <p className="text-sm leading-relaxed text-gray-600">
          Once Orbit drafts a PRD for a problem you've scoped, your build
          workspace shows up here. Bring Orbit a problem — it'll guide
          you through scoping and crafting the PRD.
        </p>
      </Card>
    );
  }
  return (
    <Card>
      <CardHead
        icon={<Hammer size={15} strokeWidth={2} />}
        title={`Your builds · ${builds.length}`}
      />
      <div className="mt-3 flex flex-col gap-2">
        {builds.map((b, i) => (
          <BuildRow key={b.project_id || i} build={b} onOpen={() => onOpenWorkspace(b)} />
        ))}
      </div>
    </Card>
  );
};


const BuildRow: React.FC<{
  build: HomeBuild;
  onOpen: () => void | Promise<void>;
}> = ({ build, onOpen }) => {
  const title = build.label || build.project_id || 'Untitled build';
  const ready = !!build.workspace_url;
  const statusLabel = ready ? 'Workspace ready' : 'Drafting';
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-white p-3 ring-1 ring-gray-200/60">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">{title}</p>
        <p className="mt-0.5 inline-flex items-center gap-1.5 text-[11px] text-gray-500">
          <span
            className={clsx(
              'inline-block h-1.5 w-1.5 rounded-full',
              ready ? 'bg-green-500' : 'bg-accent-500 animate-pulse',
            )}
          />
          {statusLabel}
        </p>
      </div>
      <button
        type="button"
        onClick={onOpen}
        disabled={!ready}
        className={clsx(
          'inline-flex flex-none items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all whitespace-nowrap',
          ready
            ? 'bg-brand-500 text-white hover:bg-brand-600 hover:-translate-y-px hover:shadow-[0_4px_20px_rgba(24,25,29,0.25)]'
            : 'cursor-not-allowed bg-gray-100 text-gray-400',
        )}
      >
        {ready ? 'Open workspace' : 'Drafting…'}
        {ready && <ChevronRight size={12} strokeWidth={2.5} />}
      </button>
    </div>
  );
};


// ── v0.5 — Profile completeness compact card ─────────────────────────
//
// Single small card with the captured/total ratio. "Tell Orbit more"
// opens the chat dock so the user can fill missing fields naturally.
const ProfileCompletenessCard: React.FC<{
  captured: number;
  total: number;
  onTellOrbit: () => void;
}> = ({ captured, total, onTellOrbit }) => {
  const pct = total > 0 ? Math.round((captured / total) * 100) : 0;
  if (pct >= 95) return null;  // Don't nag a complete profile
  return (
    <Card>
      <CardHead
        icon={<Sparkles size={15} strokeWidth={2} />}
        title="Round out the picture."
      />
      <div className="mt-2 flex items-center gap-4">
        <ProfilePrimaryRing size={56} stroke={6} pct={pct} />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 tabular-nums">
            {captured} of {total} captured.
          </p>
          <p className="mt-1 text-[12px] leading-relaxed text-gray-600">
            The more Orbit knows, the better the next build's match.
          </p>
        </div>
      </div>
      {/* 
        This "Tell Orbit more" link on the progress card has been removed because it 
        was redundant to the global "Talk to Orbit" CTA and created a confusing 
        secondary entry point.
      */}
    </Card>
  );
};



// ── Approach strip (always-visible 3-tile picker / switcher) ─────────
//
// Renders 3 cards (Automated / Paid Human / Explore). Recommended one
// is badged. Current one is badged. User can switch at any time.
// Picking calls /api/intake/approach and reloads the home payload so
// the right lane card appears in place.

const APPROACH_TILES: Array<{
  id: 'automated' | 'paid_human' | 'explore';
  title: string;
  icon: React.ReactNode;
  blurb: string;
  ring: string;
}> = [
  // Per brand: no per-tile color identity via left-edge stripes. Tiles
  // share the brand-50 surface; the hover ring is the only chrome that
  // shifts per option (all brand-500 to match the primary accent).
  {
    id: 'automated',
    title: 'Automated DIY',
    icon: <Zap size={15} strokeWidth={2.25} />,
    blurb: 'Pick a marketplace agent, template, or custom build. Move at your own pace.',
    ring: 'ring-brand-500/30',
  },
  {
    id: 'paid_human',
    title: 'Paid Human',
    icon: <UsersIcon size={15} strokeWidth={2.25} />,
    blurb: 'A Solution partner rebuilds the scope and the plan with you. Best after a bad prior attempt.',
    ring: 'ring-brand-500/30',
  },
  {
    id: 'explore',
    title: 'Explore',
    icon: <Compass size={15} strokeWidth={2.25} />,
    blurb: 'Browse the marketplace. No commitment. Come back to pick when ready.',
    ring: 'ring-brand-500/30',
  },
];

const ApproachStrip: React.FC<{
  suggestion: HomePayload['suggestion'];
  currentApproach?: string | null;
  onPicked: () => void;
  /** When true, all switch buttons are disabled and the strip reads
   *  "approach locked." Used once the engagement has reached the
   *  Experience Report milestone — switching at that point would discard
   *  scoping work. The user can still see what's selected, just not
   *  click-to-switch. */
  locked?: boolean;
}> = ({ suggestion, currentApproach, onPicked, locked }) => {
  const [picking, setPicking] = useState<'automated' | 'paid_human' | 'explore' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recommended = suggestion?.approach as 'automated' | 'paid_human' | 'explore' | undefined;
  const hasCurrent = !!currentApproach;

  const onPick = useCallback(async (id: 'automated' | 'paid_human' | 'explore') => {
    if (picking) return;
    setPicking(id);
    setError(null);
    try {
      await updateApproach(id);
      onPicked();
    } catch (e) {
      console.error('[approach-strip] failed', e);
      setError("Couldn't save your pick. Try again.");
    } finally {
      // Always release the lock — the strip stays mounted after a switch,
      // so a sticky `picking` would leave every tile disabled forever.
      setPicking(null);
    }
  }, [picking, onPicked]);

  // Title shifts based on state. Pre-pick: prompt + suggestion. Post-pick:
  // status with a soft hint. Locked: read-only label.
  const title = locked
    ? 'Approach locked.'
    : hasCurrent
      ? 'Your approach.'
      : recommended
        ? `We recommend: ${APPROACH_META[recommended]?.label ?? recommended}.`
        : 'Pick how you want to take this forward.';

  return (
    <Card>
      <CardHead icon={<Sparkles size={15} strokeWidth={2} />} title={title} />
      {!hasCurrent && !locked && suggestion?.suggestion_line && (
        <p className="text-sm leading-relaxed text-gray-600">{suggestion.suggestion_line}</p>
      )}
      {hasCurrent && !locked && (
        <p className="text-xs leading-relaxed text-gray-500">
          You can switch any time. Your scoping progress carries over.
        </p>
      )}
      {locked && (
        <p className="text-xs leading-relaxed text-gray-500">
          You've reached the experience report. Switching now would discard your scoping work, so the other paths are disabled. Pick a forward action from the report instead.
        </p>
      )}
      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3">
        {APPROACH_TILES.map(tile => {
          const isRec = recommended === tile.id;
          const isCurrent = currentApproach === tile.id;
          const isPicking = picking === tile.id;
          const disabledByPick = !!picking && !isPicking;
          const disabled = disabledByPick || (locked && !isCurrent);
          return (
            <button
              key={tile.id}
              type="button"
              onClick={() => onPick(tile.id)}
              disabled={disabled || isCurrent}
              className={clsx(
                'flex flex-col gap-2 rounded-lg bg-white p-3 text-left transition-all ring-1',
                isCurrent
                  ? 'ring-2 ring-gray-900/30'
                  : !locked && isRec
                    ? 'ring-2 ring-brand-500/30'
                    : 'ring-gray-200/70',
                disabled
                  ? 'cursor-not-allowed opacity-50'
                  : isCurrent
                    ? 'cursor-default'
                    : 'hover:-translate-y-0.5 hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:ring-brand-500/40',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="inline-flex items-center gap-1.5 text-gray-700">
                  {tile.icon}
                  <span className="text-sm font-semibold text-gray-900">{tile.title}</span>
                </div>
                {isCurrent ? (
                  <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                    {locked ? 'Locked' : 'Current'}
                  </span>
                ) : !locked && isRec && (
                  <span className="rounded-full bg-accent-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-900">
                    Recommended
                  </span>
                )}
              </div>
              <p className="text-xs leading-relaxed text-gray-600">{tile.blurb}</p>
              <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-600">
                {isPicking ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    <span>Switching.</span>
                  </>
                ) : isCurrent ? (
                  <span className="text-gray-500">Currently selected.</span>
                ) : locked ? (
                  <span className="text-gray-400">Unavailable now.</span>
                ) : hasCurrent ? (
                  <>
                    <Check size={12} strokeWidth={2.5} />
                    <span>Switch to this</span>
                  </>
                ) : (
                  <>
                    <Check size={12} strokeWidth={2.5} />
                    <span>Pick this</span>
                  </>
                )}
              </span>
            </button>
          );
        })}
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </Card>
  );
};


// ── Experience Report ready card ─────────────────────────────────────
//
// Surfaces on Overview when ≥1 problem is SCOPED but no forward path is
// picked yet. Mirrors the report's "Forward paths" section: review the
// report, OR commit to a forward path directly (Build / Training / Both).
// The three forward paths are static — defined server-side in
// experience_report.FORWARD_PATHS — so we hard-code the labels rather
// than fetching the full report just to surface three cards.

const FORWARD_PATH_TILES: Array<{
  id: 'build' | 'training';
  title: string;
  tagline: string;
  tooltip: string;
  icon: React.ReactNode;
}> = [
  // Two clear options. No "Both" — picking Build doesn't preclude a
  // future consultant call; we keep the lanes binary so the choice
  // isn't ambiguous.
  {
    id: 'build',
    title: 'Build agents now',
    tagline: 'We draft the PRD and create your workspace.',
    tooltip: "The planner reads your scope, drafts the PRD, and spins up a workspace. You review milestones at 30/60/90 days. Pick this when you're ready to ship.",
    icon: <Hammer size={15} strokeWidth={2.25} />,
  },
  {
    id: 'training',
    title: 'Talk to a Solution partner',
    tagline: 'Not ready to build yet? Book a session with our team.',
    tooltip: "A Solution partner walks through your scope with you, answers questions, and helps you decide what to ship first. Good fit if you want a human review before committing to build.",
    icon: <UsersIcon size={15} strokeWidth={2.25} />,
  },
];

const ExperienceReportReadyCard: React.FC<{
  engagementId: string;
  scopedCount: number;
  totalCount: number;
  /** Called after a forward-path pick succeeds. Parent re-fetches the
   *  home payload so the AutomatedLane re-renders with the new
   *  routing_decision.forward_path. Without this, the user is already
   *  on /my-business and `navigate('/my-business')` was a no-op — the
   *  card stayed stuck on "Locking your pick." */
  onPicked: () => void;
}> = ({ engagementId, scopedCount, totalCount, onPicked }) => {
  const navigate = useNavigate();
  const [picking, setPicking] = useState<'build' | 'training' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onPickPath = useCallback(async (id: 'build' | 'training') => {
    if (picking) return;
    setPicking(id);
    setError(null);
    try {
      await pickForwardPath(engagementId, id);
      onPicked();
    } catch (e) {
      console.error('[experience-report-ready] pick path failed', e);
      setError("Couldn't save your pick. Try again.");
      setPicking(null);
    }
  }, [engagementId, onPicked, picking]);

  return (
    <Card>
      <CardHead icon={<Sparkles size={15} strokeWidth={2} />} title="Your scoping summary is ready." />
      <p className="text-sm leading-relaxed text-gray-600">
        {scopedCount} of {totalCount} problem{totalCount === 1 ? '' : 's'} scoped.
        Pick a forward path below, or open the full report first.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
        {FORWARD_PATH_TILES.map(tile => {
          const isPicking = picking === tile.id;
          const disabled = !!picking && !isPicking;
          return (
            <button
              key={tile.id}
              type="button"
              onClick={() => onPickPath(tile.id)}
              disabled={disabled}
              title={tile.tooltip}
              className={clsx(
                'flex flex-col gap-2 rounded-lg bg-white p-3 text-left transition-all ring-1 ring-gray-200/70',
                disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:-translate-y-0.5 hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:ring-2 hover:ring-brand-500/30',
              )}
            >
              <div className="inline-flex items-center gap-1.5 text-gray-700">
                {tile.icon}
                <span className="text-sm font-semibold text-gray-900">{tile.title}</span>
              </div>
              <p className="text-xs leading-relaxed text-gray-600">{tile.tagline}</p>
              <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-600">
                {isPicking ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    <span>Locking your pick.</span>
                  </>
                ) : (
                  <>
                    <Check size={12} strokeWidth={2.5} />
                    <span>Pick this path</span>
                  </>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
        <p className="text-xs text-gray-500">
          Read the full report for the proposals, scoped agents, and 30/60/90-day plan.
        </p>
        <button
          type="button"
          onClick={() => navigate(`/experience-report/${engagementId}`)}
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700"
        >
          Review your scoping summary
          <ChevronRight size={12} strokeWidth={2.5} />
        </button>
      </div>
    </Card>
  );
};


// ── Lane cards ────────────────────────────────────────────────────────

const PaidHumanLane: React.FC<{
  consultant: HomePayload['consultant'];
  engagement: HomePayload['engagement'];
}> = ({ consultant, engagement }) => (
  <section className="rounded-xl bg-brand-50 px-5 py-4 ring-1 ring-gray-200/60">
    <header className="mb-3 flex items-center gap-2 text-gray-700">
      <UsersIcon size={15} strokeWidth={2} />
      <h3 className="text-sm font-semibold leading-snug text-gray-900">Your Solution partner.</h3>
    </header>
    {consultant ? (
      <div className="flex flex-col gap-3">
        <p className="text-sm leading-relaxed text-gray-600">
          A Solution partner from the Agents Anywhere team is assigned to your scoping. Book
          a time below. They'll run scoping with you and push updates here.
        </p>
        <div className="flex items-center gap-3 rounded-lg bg-white px-3 py-2.5 ring-1 ring-gray-200/60">
          <div className="grid h-8 w-8 flex-none place-items-center rounded-full bg-brand-500/10 text-brand-600">
            <UserIcon size={14} strokeWidth={2.25} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-gray-900">
              {consultant.name || 'Agents Anywhere team'}
            </div>
            {consultant.email && (
              <a
                href={`mailto:${consultant.email}`}
                className="truncate text-[11px] text-brand-600 hover:underline"
              >
                {consultant.email}
              </a>
            )}
          </div>
        </div>
        {consultant.meeting_url ? (
          <a
            href={consultant.meeting_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-fit items-center gap-1.5 rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-brand-600 hover:-translate-y-px hover:shadow-[0_4px_20px_rgba(24,25,29,0.25)] whitespace-nowrap"
          >
            <Sparkles size={13} strokeWidth={2.25} />
            <span>Book a scoping session</span>
            <ExternalLink size={12} strokeWidth={2.25} />
          </a>
        ) : consultant.email ? (
          <CardCTA
            href={`mailto:${consultant.email}?subject=Scoping session for ${engagement?.id ?? ''}`}
            label="Email to schedule"
          />
        ) : null}
      </div>
    ) : (
      <p className="text-sm leading-relaxed text-gray-600">
        We've notified the team. A Solution partner will reach out at the email on your account.
      </p>
    )}
  </section>
);

const AutomatedLane: React.FC<{
  home: HomePayload;
  onOpenWorkspace: (build: HomeBuild) => void | Promise<void>;
  onContinue: () => void;
  /** Refetches the home payload. Threaded down from the page so child
   *  cards (forward-path picker, etc.) can trigger a state refresh
   *  without the parent re-mounting — `navigate('/my-business')` is a
   *  no-op when the user is already on the route. */
  onReload: () => void;
}> = ({ home, onOpenWorkspace, onContinue, onReload }) => {
  const navigate = useNavigate();
  const build = home.builds[0];
  const engagementStatus = home.engagement?.status || '';

  // 1) Build exists → split state by PRD readiness.
  if (build?.workspace_url) {
    const prdReady = engagementStatus === 'PRD_READY' || engagementStatus === 'IN_PROGRESS' || engagementStatus === 'COMPLETED';
    if (prdReady) {
      return (
        <Card>
          <CardHead icon={<Zap size={15} strokeWidth={2} />} title="PRD ready to review." />
          <p className="text-sm leading-relaxed text-gray-600">
            The detailed work lives in your workspace. Open it to read the PRD, edit, and lock to build.
          </p>
          <CardCTA onClick={() => onOpenWorkspace(build)} label="Open workspace" />
        </Card>
      );
    }
    // Project created but PRD still being drafted from dossier in background.
    return (
      <Card>
        <CardHead
          icon={
            <span className="relative inline-flex">
              <Zap size={15} strokeWidth={2} />
              <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 animate-pulse rounded-full bg-brand-500" />
            </span>
          }
          title="Drafting your PRD."
        />
        <p className="text-sm leading-relaxed text-gray-600">
          The workspace is set up. The planner is reading your brief and
          writing the PRD now. Usually under a minute.
        </p>
        <div className="mt-3 flex items-center gap-3">
          <p className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500">
            <Loader2 size={11} className="animate-spin" />
            Drafting.
          </p>
          <button
            type="button"
            onClick={() => onOpenWorkspace(build)}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-600 hover:text-brand-700"
          >
            Open workspace
            <ChevronRight size={10} strokeWidth={2.5} />
          </button>
        </div>
      </Card>
    );
  }

  const eid = home.engagement?.id;
  const routingDecision = home.engagement?.routing_decision as {
    stage1_pick?: string;
    stage2_decision?: { outcome?: string };
    forward_path?: string;
  } | undefined;
  const stage1Picked = !!routingDecision?.stage1_pick;
  const stage2Outcome = routingDecision?.stage2_decision?.outcome;
  const forwardPathPicked = !!routingDecision?.forward_path;
  const problemsSummary = home.problems_summary || { total: 0, scoped: 0 };
  // Experience Report is ready once at least one problem hits SCOPED.
  // We stop surfacing it once the user picks a forward path (build /
  // training / scope-more) — that flips the lane to the next state.
  const experienceReportReady = problemsSummary.scoped > 0 && !forwardPathPicked;

  // 2) Stage 1 not picked yet → CTA card linking to the routed
  //    /orbit/options/:eid Stage 1 page. The page is dashboard-wrapped
  //    so the customer doesn't lose the sidebar when picking a path.
  if (eid && !stage1Picked) {
    return (
      <Card>
        <CardHead icon={<Zap size={15} strokeWidth={2} />} title="Pick a solution path." />
        <p className="text-sm leading-relaxed text-gray-600">
          We've matched your brief to three options — a marketplace agent,
          a customized template, and a custom build. Pick one and we keep moving.
        </p>
        <CardCTA onClick={() => navigate(`/orbit/options/${eid}`)} label="See solution options" />
      </Card>
    );
  }

  // 2.5) Experience Report ready — Phase 2 closed (≥1 problem SCOPED)
  //      and the user hasn't picked a forward path yet. This is the
  //      milestone-artifact moment: review the auto-draft, then pick
  //      Build / Training / Both right here on Overview (mirror of the
  //      report's forward-paths section so the user can act without
  //      jumping pages — but still has "Review the report" as the
  //      considered path).
  if (eid && experienceReportReady) {
    return <ExperienceReportReadyCard
      engagementId={eid}
      scopedCount={problemsSummary.scoped}
      totalCount={problemsSummary.total}
      onPicked={onReload}
    />;
  }

  // 2.6) Forward path picked from the Experience Report. We override the
  //      generic "Phase 1 scoping is next" fallback so the user doesn't
  //      get told to redo scoping. Each path surfaces its own next-step
  //      card. Build paths fall through to the existing PRD-drafting
  //      flow (case 3) once the engagement.status advances; while we
  //      wait for that, we show a "kicking off" card. Training has no
  //      validx project, so we show a training-program card and link
  //      back to the report where the plan lives.
  const forwardPath = routingDecision?.forward_path;
  if (eid && forwardPathPicked) {
    // Training (= "Talk to a consultant"). The whole intent is a booked
    // session with the team — surface the cal.com booking link, drop any
    // PRD / experience-report copy so the user doesn't get sent in
    // circles. Consultant URL env-overridable; cal.com link is the
    // default already used by the Burned upsell banner in the report.
    if (forwardPath === 'training') {
      const bookingUrl = process.env.REACT_APP_CONSULTANT_CAL_URL
        || 'https://cal.com/team-brndsh/review-session';
      return (
        <Card primary>
          <CardHead icon={<UsersIcon size={15} strokeWidth={2} />} title="Book your Solution partner session." />
          <p className="text-sm leading-relaxed text-gray-600">
            A Solution partner from our team will walk through your scope with you,
            answer questions, and help you pick what to ship first. Single
            session, no commitment.
          </p>
          <a
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-brand-600 hover:-translate-y-px hover:shadow-[0_4px_20px_rgba(24,25,29,0.25)] whitespace-nowrap"
          >
            <span>Book a session</span>
            <ExternalLink size={13} strokeWidth={2.25} />
          </a>
        </Card>
      );
    }
    // Build picked but no validx project has spawned yet. (Legacy 'both'
    // engagements ride the same lane — the build half is what's visible
    // here.) Once the PROJECT_CREATED webhook lands, case 1 takes over.
    if ((forwardPath === 'build' || forwardPath === 'both') && !build) {
      return (
        <Card primary>
          <CardHead
            icon={
              <span className="relative inline-flex">
                <Zap size={15} strokeWidth={2} />
                <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 animate-pulse rounded-full bg-brand-500" />
              </span>
            }
            title="Build kicking off."
          />
          <p className="text-sm leading-relaxed text-gray-600">
            The planner is reading your scope and drafting the PRD. You'll
            get a workspace link as soon as the build environment is ready.
            Usually under a minute.
          </p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500">
            <Loader2 size={11} className="animate-spin" />
            Waiting for the workspace.
          </p>
        </Card>
      );
    }
  }

  // 3) Stage 1 picked, planner auto-trigger fires in the background, but
  // the PROJECT_CREATED webhook hasn't landed yet. Show a "drafting"
  // state with a pulse animation so the user knows work is happening.
  if (eid && stage1Picked && stage2Outcome && stage2Outcome !== 'l2_required') {
    const label = stage2Outcome === 'template_direct'
      ? 'Drafting your PRD from the template.'
      : stage2Outcome === 'planner_direct'
        ? 'Drafting your PRD now.'
        : 'Setting things up.';
    return (
      <Card>
        <CardHead
          icon={
            <span className="relative inline-flex">
              <Zap size={15} strokeWidth={2} />
              <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 animate-pulse rounded-full bg-brand-500" />
            </span>
          }
          title={label}
        />
        <p className="text-sm leading-relaxed text-gray-600">
          The planner is reading your brief and drafting the spec.
          You'll get a workspace link as soon as it's ready. Usually
          under a minute.
        </p>
        <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500">
          <Loader2 size={11} className="animate-spin" />
          Waiting for the workspace.
        </p>
      </Card>
    );
  }

  // 4) Stage 1 picked but outcome is l2_required → continue scoping.
  //    Phase-aware CTA labels so the user knows EXACTLY what step's next:
  //    Phase 1 (Company Shell) → Phase 2 (Problems) → Phase 3 (Synthesis).
  if (eid && stage1Picked && stage2Outcome === 'l2_required') {
    return (
      <Card>
        <CardHead icon={<Zap size={15} strokeWidth={2} />} title="Phase 1 scoping is next." />
        <p className="text-sm leading-relaxed text-gray-600">
          Your pick needs a deeper look before we draft the PRD. Orbit
          walks through Phase 1 (strategic context, tools, buying committee,
          compliance), then Phase 2 (per-problem scoping), then auto-drafts
          Phase 3 synthesis on the Experience Report.
        </p>
        <CardCTA onClick={onContinue} label="Start Phase 1 with Orbit" />
      </Card>
    );
  }

  // 5) Default fallback — no engagement context to act on.
  return (
    <Card>
      <CardHead icon={<Zap size={15} strokeWidth={2} />} title="Pick a build direction." />
      <p className="text-sm leading-relaxed text-gray-600">
        Marketplace agent, customized template, or a custom build. We
        match against your brief and surface the three options inline.
      </p>
      <CardCTA onClick={onContinue} label="See solution options" />
    </Card>
  );
};


// ── Stage 1 inline picker ─────────────────────────────────────────────

const STAGE1_KIND_META: Record<SolutionOptionKind, {
  badge: string;
  icon: React.ReactNode;
  blurb: string;
}> = {
  marketplace: { badge: 'Marketplace', icon: <ShoppingBag size={14} strokeWidth={2.25} />, blurb: 'Pre-built. Deploy in days.' },
  template:    { badge: 'Template',    icon: <FileText size={14} strokeWidth={2.25} />,    blurb: 'Configured to your context.' },
  custom:      { badge: 'Custom',      icon: <Hammer size={14} strokeWidth={2.25} />,      blurb: 'Built fresh for your spec.' },
};

const Stage1InlinePicker: React.FC<{
  engagementId: string;
  onPicked: () => void;
}> = ({ engagementId, onPicked }) => {
  const navigate = useNavigate();
  const [state, setState] = useState<Stage1Response | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState<SolutionOptionKind | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchStage1Options(engagementId);
        if (!cancelled) setState(data);
      } catch (e) {
        console.error('[stage1-inline] fetch failed', e);
        if (!cancelled) setError("Couldn't load options. Try refreshing.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [engagementId]);

  const onPick = useCallback(async (card: SolutionOptionCard) => {
    if (picking) return;
    setPicking(card.kind);
    try {
      const res = await pickStage1Option(engagementId, card.kind, card.solution_slug);
      // Stage 2 outcome decides where the user goes next.
      const outcome: BypassOutcome = res.decision.outcome;
      const route = res.decision.next_route;
      if (outcome === 'l2_required') {
        // L2 lives in the right-rail chat now — stay on MyBusiness and
        // let the chat handle the deeper questions. Caller refreshes
        // the home payload so the lane card updates.
        window.dispatchEvent(new CustomEvent('orbit:open'));
        onPicked();
      } else if (route) {
        // Direct deploy, template config, planner direct — these
        // remain on dedicated routes for now until Phase 3 collapses
        // them into MyBusinessHome surfaces.
        navigate(route);
      } else {
        onPicked();
      }
    } catch (e) {
      console.error('[stage1-inline] pick failed', e);
      setError("Couldn't save your pick. Try again.");
    } finally {
      setPicking(null);
    }
  }, [engagementId, navigate, onPicked, picking]);

  if (loading) {
    return (
      <Card>
        <CardHead icon={<Zap size={15} strokeWidth={2} />} title="Loading options." />
        <p className="text-sm text-gray-500">Matching your brief to what we can ship.</p>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <CardHead icon={<Zap size={15} strokeWidth={2} />} title="Couldn't load options." />
        <p className="text-sm text-gray-600">{error}</p>
      </Card>
    );
  }
  if (!state) return null;
  if (!state.eligible) {
    return (
      <Card>
        <CardHead icon={<Zap size={15} strokeWidth={2} />} title="A bit more scoping first." />
        <p className="text-sm text-gray-600">
          {state.ineligible_reason || 'Talk to Orbit to fill the deeper questions, then we will surface options.'}
        </p>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('orbit:open'))}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-brand-500 whitespace-nowrap"
        >
          <MessageSquare size={13} strokeWidth={2.25} />
          Talk to Orbit
        </button>
      </Card>
    );
  }

  return (
    <Card>
      <CardHead icon={<Zap size={15} strokeWidth={2} />} title="Pick a build direction." />
      <p className="text-sm text-gray-600 mb-3">
        Three matches from your brief. Pick one and we keep moving.
      </p>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        {state.cards.map(card => (
          <Stage1OptionTile
            key={card.kind}
            card={card}
            picking={picking === card.kind}
            disabled={!!picking && picking !== card.kind}
            onPick={() => onPick(card)}
          />
        ))}
      </div>
    </Card>
  );
};

const Stage1OptionTile: React.FC<{
  card: SolutionOptionCard;
  picking: boolean;
  disabled: boolean;
  onPick: () => void;
}> = ({ card, picking, disabled, onPick }) => {
  const meta = STAGE1_KIND_META[card.kind];
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      className={clsx(
        'flex flex-col gap-2 rounded-lg border bg-white p-3 text-left transition-colors',
        disabled
          ? 'cursor-not-allowed border-gray-200 opacity-50'
          : 'border-gray-200 hover:border-brand-500 hover:bg-brand-50',
      )}
    >
      <div className="flex items-center gap-1.5 text-brand-600">
        {meta.icon}
        <span className="text-[11px] font-semibold uppercase tracking-wide">{meta.badge}</span>
      </div>
      <h4 className="text-sm font-semibold text-gray-900 leading-tight">{card.name}</h4>
      <p className="text-xs leading-relaxed text-gray-600">{card.tagline || meta.blurb}</p>
      <div className="mt-1 flex flex-wrap gap-1.5 text-[11px]">
        {card.price_summary && (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">{card.price_summary}</span>
        )}
        {card.timeline_summary && (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">{card.timeline_summary}</span>
        )}
      </div>
      <div className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-600">
        {picking ? (
          <>
            <Loader2 size={12} className="animate-spin" />
            <span>Picking.</span>
          </>
        ) : (
          <>
            <Check size={12} strokeWidth={2.5} />
            <span>Pick this</span>
          </>
        )}
      </div>
    </button>
  );
};

const ExploreLane: React.FC = () => (
  <Card>
    <CardHead icon={<Compass size={15} strokeWidth={2} />} title="Browse what we ship." />
    <p className="text-sm leading-relaxed text-gray-600">
      No commitment. See the marketplace agents we can deploy today, and come back to pick when ready.
    </p>
    <CardCTA href="/marketplace" label="Open marketplace" />
  </Card>
);


// ── Dossier tab — side-by-side grid of editable section cards ────────
//
// Default view: ALL sections rendered side by side in a responsive grid.
// Toggle: filter pills above the grid let the user narrow to a single
// section. Each row is editable inline via <EditableField> — a
// dashboard-native Tailwind row that opens the right control (chips,
// input, textarea) on click. Confirm/correct hits /api/intake/confirm;
// the home reloads so values + states refresh.

interface DossierLayerTabProps {
  layer: 'L1' | 'L2';
  fields: FieldDef[];
  sectionLabels: Record<string, string>;
  companyContext: HomePayload['company_context'];
  emptyStateSubtitle: string;
  clientId: string;
  onCommitted: () => void;
  /** Enables the "Share with teammate" affordance on each section card.
   *  Only meaningful on L2 (Phase 1) — the scope-share backend issues a
   *  JWT scoped to {engagement, section, layer, expires}. L1 sections
   *  (A–G) are personal company-identity fields, not delegated. */
  showShareButton?: boolean;
  /** Engagement id for the Share endpoint. When `showShareButton` is
   *  true but this is null, the share button stays hidden — the
   *  delegation token is bound to a specific engagement. */
  engagementId?: string | null;
}

// ── Dossier full tab — L1 + L2 in one scrollable view ───────────────
//
// Renders L1 first (who you are), then L2 (your project) with a soft
// divider. L2 is only meaningful once an approach is picked, so the
// section either shows the same scan cards or a gentle "unlocks after
// you pick an approach" empty state. Either way, the user sees their
// whole dossier surface in one place + can switch to deep work on the
// Scope page when they're ready.

// ── Company Context tab — L1 sections A–G ────────────────────────────
//
// Renamed from "Brief" per the new IA. Same DossierLayerTab primitive
// scoped to layer='L1'. Sub-headers group by intent (Identity / Stack /
// Org / Goals / Problem / Budget / Engagement) so the sections aren't
// just letters A–G to the user.

const AllFieldsDisclosure: React.FC<{
  fields: FieldDef[];
  sectionLabels: Record<string, string>;
  companyContext: HomePayload['company_context'];
  clientId: string;
  onCommitted: () => void;
}> = ({ fields, sectionLabels, companyContext, clientId, onCommitted }) => {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-2xl ring-1 ring-gray-200/60">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 px-5 py-4 text-left"
      >
        <span className="text-[13.5px] font-semibold text-gray-900">Every field, editable</span>
        <span className="text-[12px] text-gray-400">
          Edits save themselves. No Save button.
        </span>
        <ChevronRight
          size={16}
          strokeWidth={2.25}
          className={clsx('ml-auto shrink-0 text-gray-400 transition-transform', open && 'rotate-90')}
        />
      </button>
      {open && (
        <div className="border-t border-gray-100 px-5 py-4">
          <CompanyContextTab
            fields={fields}
            sectionLabels={sectionLabels}
            companyContext={companyContext}
            clientId={clientId}
            onCommitted={onCommitted}
          />
        </div>
      )}
    </section>
  );
};


const CompanyContextTab: React.FC<{
  fields: FieldDef[];
  sectionLabels: Record<string, string>;
  companyContext: HomePayload['company_context'];
  clientId: string;
  onCommitted: () => void;
}> = ({ fields, sectionLabels, companyContext, clientId, onCommitted }) => (
  <div className="flex flex-col gap-6">
    <DossierLayerTab
      layer="L1"
      fields={fields}
      sectionLabels={sectionLabels}
      companyContext={companyContext}
      emptyStateSubtitle="The company brief — who you are."
      clientId={clientId}
      onCommitted={onCommitted}
    />
  </div>
);


// ── Scoping tab — L2 Phase 1 (Company Shell) editable grid ──────────
//
// Renders H / I / M / N / O as section cards using the exact same
// DossierLayerTab primitive that powers Company Context (A–G). The user
// can fill any field manually here, or let Orbit fill it via chat, or
// share a section with a teammate via the "Share" button.
//
// Phase 2 (per-problem cards looped) lives on /orbit/scope — each card
// has its own conversation, so it can't render as a flat dossier grid.
// Phase 3 (K + P synthesis) is auto-drafted on the Experience Report
// from accumulated Phase 1 + Phase 2 data, never user-filled directly.
// We point users to those surfaces with a slim footer note instead of
// reserving empty placeholders.

const ScopingTab: React.FC<{
  fields: FieldDef[];
  sectionLabels: Record<string, string>;
  companyContext: HomePayload['company_context'];
  clientId: string;
  engagementId: string | null;
  onCommitted: () => void;
  available: boolean;
}> = ({ fields, sectionLabels, companyContext, clientId, engagementId, onCommitted, available }) => {
  if (!available) {
    return (
      <EmptyState
        icon={<Compass size={28} strokeWidth={1.5} />}
        title="Scoping unlocks once you pick an approach."
        subtitle="Finish your brief and pick a direction (marketplace, customized template, or custom build) to start scoping."
      />
    );
  }

  const phase1Letters = ['h', 'i', 'm', 'n', 'o'];
  const phase1Fields = fields.filter(
    f => (f.layer || 'L1') === 'L2' && phase1Letters.includes(f.section),
  );

  return (
    <div className="flex flex-col gap-5">
      <LayerHeader
        label="Scoping"
        subtitle="The five cross-cutting areas every build reads from. Fill any field directly, talk to Orbit, or share a section with the right teammate."
      />

      <DossierLayerTab
        layer="L2"
        fields={phase1Fields}
        sectionLabels={sectionLabels}
        companyContext={companyContext}
        emptyStateSubtitle="Pick an approach and these sections start filling."
        clientId={clientId}
        engagementId={engagementId}
        showShareButton
        onCommitted={onCommitted}
      />
    </div>
  );
};


// Phase sub-header used inside Scoping tab.
const PhaseSubheader: React.FC<{ phase: string; label: string; subtitle: string }> = ({
  phase, label, subtitle,
}) => (
  <header className="mb-3 flex items-baseline justify-between gap-3 border-b border-gray-100 pb-2">
    <div>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-brand-600">
        {phase} · {label}
      </h2>
      <p className="mt-0.5 text-[11px] text-gray-500">{subtitle}</p>
    </div>
  </header>
);


const LayerHeader: React.FC<{ label: string; subtitle: string }> = ({ label, subtitle }) => (
  <header className="mb-3 flex items-baseline justify-between gap-3 border-b border-gray-100 pb-2">
    <div>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</h2>
      <p className="mt-0.5 text-[11px] text-gray-400">{subtitle}</p>
    </div>
  </header>
);


const LEVERAGE_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };

function prioritizeFields(fields: FieldDef[]): FieldDef[] {
  // Sort by: critical first, then required-for-routing, then leverage,
  // finally registry order (key). Drives which 3 fields show in the
  // summary card preview — the highest-signal three per section.
  return [...fields].sort((a, b) => {
    const aCrit = a.critical ? 0 : 1;
    const bCrit = b.critical ? 0 : 1;
    if (aCrit !== bCrit) return aCrit - bCrit;
    const aReq = a.required_for_routing ? 0 : 1;
    const bReq = b.required_for_routing ? 0 : 1;
    if (aReq !== bReq) return aReq - bReq;
    const aLev = LEVERAGE_RANK[a.leverage] ?? 3;
    const bLev = LEVERAGE_RANK[b.leverage] ?? 3;
    if (aLev !== bLev) return aLev - bLev;
    return a.key.localeCompare(b.key);
  });
}


const DossierLayerTab: React.FC<DossierLayerTabProps> = ({
  layer, fields, sectionLabels, companyContext, emptyStateSubtitle,
  clientId, onCommitted, showShareButton, engagementId,
}) => {
  const [filter, setFilter] = useState<'all' | SectionLetter>('all');
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [shareSection, setShareSection] = useState<string | null>(null);

  // Hidden fields are dropped, not greyed: a question this company will never
  // be asked is not part of their roadmap, and counting it is what made the
  // rail read "O 0/4" for a non-regulated agency.
  const layerFields = useMemo(
    () => fields.filter(f => (f.layer || 'L1') === layer && !f.hidden),
    [fields, layer],
  );

  const allLetters = useMemo(() => {
    const set = new Set<string>();
    layerFields.forEach(f => set.add(f.section));
    return Array.from(set).sort();
  }, [layerFields]);

  // Rethrows on purpose. B4's autosave reverts the row when the write fails,
  // and it can only do that if the failure reaches it — swallowing the error
  // here left the refused value sitting on screen as though it had saved.
  // Same reason as the onboarding cards: this page writes rows, it does not
  // hold a conversation, so it does not pay for the two model calls that
  // advancing one costs. That wait was measured at nine to twenty-one seconds
  // per row and read as a row that had not saved.
  const onCommit = useCallback(async (key: string, value: unknown, action: 'confirm' | 'correct') => {
    await confirmField(key, value, action, clientId, undefined, false);
    onCommitted();
  }, [clientId, onCommitted]);

  if (layerFields.length === 0) {
    return (
      <EmptyState
        icon={<Sparkles size={28} strokeWidth={1.5} />}
        title={layer === 'L2' ? 'Scoping not started.' : 'No fields yet.'}
        subtitle={emptyStateSubtitle}
      />
    );
  }

  const visibleLetters = filter === 'all'
    ? allLetters
    : allLetters.filter(l => l === filter);

  const openSectionFields = openSection
    ? layerFields.filter(f => f.section === openSection)
    : [];

  return (
    <div className="flex flex-col gap-3">
      {/* Filter pills — default 'All' renders the full grid */}
      <nav className="flex flex-wrap gap-1.5" role="tablist" aria-label="Filter dossier by section">
        <SectionFilterPill
          letter={null}
          label="All"
          active={filter === 'all'}
          captured={layerFields.filter(f => {
            const st = companyContext.field_states[f.key]?.state;
            return st === 'user_confirmed' || st === 'user_corrected' || st === 'ai_guessed';
          }).length}
          total={layerFields.length}
          onClick={() => setFilter('all')}
        />
        {allLetters.map(letter => {
          const sectionFields = layerFields.filter(f => f.section === letter);
          const captured = sectionFields.filter(f => {
            const st = companyContext.field_states[f.key]?.state;
            return st === 'user_confirmed' || st === 'user_corrected' || st === 'ai_guessed';
          }).length;
          return (
            <SectionFilterPill
              key={letter}
              letter={letter}
              label={sectionLabels[letter] || `Section ${letter.toUpperCase()}`}
              active={filter === letter}
              captured={captured}
              total={sectionFields.length}
              onClick={() => setFilter(letter as SectionLetter)}
            />
          );
        })}
      </nav>

      {/* Equal-size summary cards — click any card to open the section
          drawer with all fields editable inline. Optional Share button
          (L2 only) hands the section off to a teammate via the scope
          delegation backend. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {visibleLetters.map(letter => {
          const sectionFields = layerFields.filter(f => f.section === letter);
          return (
            <SectionSummaryCard
              key={letter}
              letter={letter}
              title={sectionLabels[letter] || `Section ${letter.toUpperCase()}`}
              fields={sectionFields}
              companyContext={companyContext}
              onOpen={() => setOpenSection(letter)}
              onShare={showShareButton && engagementId
                ? () => setShareSection(letter)
                : undefined}
            />
          );
        })}
      </div>

      {/* Right-side drawer — full editable list of the open section.
          Slides in alongside the existing chat drawer (z-index ordered
          so chat sits above when both are open). */}
      <SectionEditDrawer
        open={!!openSection}
        letter={openSection || ''}
        title={openSection ? (sectionLabels[openSection] || `Section ${openSection.toUpperCase()}`) : ''}
        fields={openSectionFields}
        companyContext={companyContext}
        onClose={() => setOpenSection(null)}
        onCommit={onCommit}
      />

      {/* Per-section share modal — issues a JWT scoped to one section so
          the right teammate can fill JUST that area. */}
      {shareSection && engagementId && (
        <SectionShareModal
          engagementId={engagementId}
          layer={layer}
          section={shareSection}
          sectionLabel={sectionLabels[shareSection] || `Section ${shareSection.toUpperCase()}`}
          onClose={() => setShareSection(null)}
        />
      )}
    </div>
  );
};


// ── Summary card ─────────────────────────────────────────────────────

const SectionSummaryCard: React.FC<{
  letter: string;
  title: string;
  fields: FieldDef[];
  companyContext: HomePayload['company_context'];
  onOpen: () => void;
  /** Optional Share affordance — when provided, a small "Share" icon
   *  button renders in the card head and calls this on click. */
  onShare?: () => void;
}> = ({ letter, title, fields, companyContext, onOpen, onShare }) => {
  const captured = fields.filter(f => {
    const st = companyContext.field_states[f.key]?.state;
    return st === 'user_confirmed' || st === 'user_corrected' || st === 'ai_guessed';
  }).length;
  const pct = fields.length > 0 ? Math.round((captured / fields.length) * 100) : 0;
  const topFields = prioritizeFields(fields).slice(0, 3);

  // Card is a div (not a button) so we can nest the Share button
  // legally. Click anywhere outside the Share button opens the section.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      className="group flex h-full cursor-pointer flex-col rounded-xl bg-brand-50 p-4 text-left ring-1 ring-gray-200/60 transition-all duration-150 hover:-translate-y-0.5 hover:ring-brand-500/40 hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)]"
    >
      {/* Head — section badge + title + count + (optional) share */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <span className="rounded-md bg-brand-500 px-1.5 py-0.5 font-sans text-[10px] uppercase tracking-wider text-white">
            {letter}
          </span>
          <span className="truncate">{title}</span>
        </h3>
        <div className="flex flex-none items-center gap-2">
          <span className="text-xs tabular-nums text-gray-500">
            {captured}/{fields.length}
          </span>
          {onShare && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onShare(); }}
              className="grid h-6 w-6 place-items-center rounded-md text-gray-500 transition-colors hover:bg-white hover:text-brand-600"
              title="Share this section with a teammate"
              aria-label="Share with teammate"
            >
              <Share2 size={12} strokeWidth={2.25} />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-2 h-1 overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full bg-brand-500 transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Top 3 fields preview — compact mini rows */}
      <ul className="mt-3 flex flex-1 flex-col gap-2">
        {topFields.map(f => {
          const sectionKey = `section_${f.section}` as keyof CompanyContext;
          const section = companyContext[sectionKey] as Record<string, unknown> | undefined;
          const value = section?.[f.key.split('.')[1]];
          const state: FieldState = (companyContext.field_states[f.key]?.state as FieldState) || 'empty';
          const badge = STATE_BADGE[state];
          const valueStr = fmtValue(f, value);
          return (
            <li key={f.key} className="flex items-start gap-2 text-xs">
              <span className={clsx('mt-0.5 flex-none', badge.tone)} aria-label={badge.label}>
                {badge.icon}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-medium text-gray-700">{f.label}</span>
                  {f.critical && (
                    <Star
                      size={10}
                      strokeWidth={2.5}
                      className="text-accent-600"
                      aria-label="critical for routing"
                    />
                  )}
                </div>
                {valueStr ? (
                  <p className="mt-0.5 truncate text-[11px] text-gray-500">{valueStr}</p>
                ) : (
                  <p className="mt-0.5 font-serif text-[11px] italic text-gray-400">Not filled.</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Footer hint — opens drawer */}
      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2 text-[11px]">
        <span className="text-gray-500">
          {fields.length - topFields.length > 0
            ? `+${fields.length - topFields.length} more in this section`
            : `Open to edit`}
        </span>
        <span className="inline-flex items-center gap-0.5 font-medium text-gray-400 transition-colors group-hover:text-brand-600">
          Open
          <ChevronRight size={11} strokeWidth={2.5} />
        </span>
      </div>
    </div>
  );
};


// ── Section share modal ──────────────────────────────────────────────
//
// Issues a delegation token for one section so a teammate can fill it.
// Backend is already wired (scope_share_token + /api/scope/{eid}/share);
// the modal hits the endpoint, gets back a `share_url` like
// `/orbit/delegated/<token_id>`, and lets the owner copy it. Recipient
// page (/orbit/delegated/<token>) is the deferred 120-line surface that
// still needs to ship — until then, the share URL is the canonical link
// the owner sends, and the recipient can authenticate manually if
// they're already a teammate on the workspace.

const SectionShareModal: React.FC<{
  engagementId: string;
  layer: 'L1' | 'L2';
  section: string;
  sectionLabel: string;
  onClose: () => void;
}> = ({ engagementId, layer, section, sectionLabel, onClose }) => {
  const [email, setEmail] = useState('');
  const [expiresDays, setExpiresDays] = useState(14);
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const onIssue = useCallback(async () => {
    if (issuing) return;
    setIssuing(true);
    setError(null);
    try {
      const res = await issueScopeShareToken({
        engagementId,
        section,
        layer,
        recipient_email: email.trim() || undefined,
        expires_days: expiresDays,
      });
      const fullUrl = `${window.location.origin}${res.share_url}`;
      setShareUrl(fullUrl);
    } catch (e) {
      console.error('[share-modal] issue failed', e);
      setError("Couldn't create the share link. Try again.");
    } finally {
      setIssuing(false);
    }
  }, [issuing, engagementId, section, layer, email, expiresDays]);

  const onCopy = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('[share-modal] copy failed', e);
    }
  }, [shareUrl]);

  return (
    <>
      <button
        type="button"
        aria-label="Close share dialog"
        onClick={onClose}
        className="fixed inset-0 z-[95] bg-black/20 backdrop-blur-[1px]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
        className="fixed left-1/2 top-1/2 z-[100] w-[min(92vw,480px)] -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-5 shadow-[0_24px_64px_rgba(0,0,0,0.18)] ring-1 ring-gray-200/60 font-sans"
      >
        <header className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 id="share-modal-title" className="text-base font-semibold text-gray-900">
              Share {sectionLabel.toLowerCase()} with a teammate.
            </h2>
            <p className="mt-0.5 text-xs leading-relaxed text-gray-600">
              They'll get a focused page to fill just this section. No other dossier data is visible to them.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-7 w-7 flex-none place-items-center rounded-md text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </header>

        {!shareUrl ? (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium text-gray-700">Recipient email (optional)</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="cto@yourcompany.com"
                className="rounded-md bg-white px-3 py-2 text-sm text-gray-900 ring-1 ring-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
              />
              <span className="text-[10px] text-gray-500">If set, only this address can open the link.</span>
            </label>

            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium text-gray-700">Link expires after</span>
              <select
                value={expiresDays}
                onChange={(e) => setExpiresDays(Number(e.target.value))}
                className="rounded-md bg-white px-3 py-2 text-sm text-gray-900 ring-1 ring-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
              >
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </label>

            {error && <p className="text-xs text-red-600">{error}</p>}

            <div className="mt-2 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onIssue}
                disabled={issuing}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-brand-600 hover:-translate-y-px hover:shadow-[0_4px_20px_rgba(24,25,29,0.25)] disabled:opacity-50 disabled:hover:translate-y-0 whitespace-nowrap"
              >
                {issuing ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    Creating link.
                  </>
                ) : (
                  'Create share link'
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-xs leading-relaxed text-gray-700">
              Send this link to your teammate. They'll land on a focused page for {sectionLabel.toLowerCase()} only.
            </p>
            <div className="flex items-center gap-2 rounded-md bg-brand-50 px-3 py-2 ring-1 ring-gray-200/60">
              <code className="flex-1 truncate font-sans text-xs text-gray-900">{shareUrl}</code>
              <button
                type="button"
                onClick={onCopy}
                className="inline-flex flex-none items-center gap-1 rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 whitespace-nowrap"
              >
                {copied ? (
                  <>
                    <Check size={11} strokeWidth={2.5} />
                    Copied
                  </>
                ) : (
                  'Copy'
                )}
              </button>
            </div>
            <p className="text-[10px] leading-relaxed text-gray-500">
              The recipient page itself is shipping in a follow-up. For now, the link is bound to the section and engagement — already-signed-in teammates can use it as a focused entry point.
            </p>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};


// ── Section edit drawer ──────────────────────────────────────────────

const SectionEditDrawer: React.FC<{
  open: boolean;
  letter: string;
  title: string;
  fields: FieldDef[];
  companyContext: HomePayload['company_context'];
  onClose: () => void;
  onCommit: (key: string, value: unknown, action: 'confirm' | 'correct') => Promise<unknown>;
}> = ({ open, letter, title, fields, companyContext, onClose, onCommit }) => {
  const captured = fields.filter(f => {
    const st = companyContext.field_states[f.key]?.state;
    return st === 'user_confirmed' || st === 'user_corrected' || st === 'ai_guessed';
  }).length;

  // Esc closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <aside
        className={clsx(
          'fixed top-0 right-0 z-[70] flex h-full w-full max-w-[520px] flex-col border-l border-gray-200 bg-white shadow-2xl transition-transform duration-200',
          open ? 'translate-x-0' : 'translate-x-full pointer-events-none',
        )}
        aria-hidden={!open}
        aria-label={`${title} fields`}
      >
        {/* Header */}
        <header className="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-3.5">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="rounded-md bg-gray-900 px-1.5 py-0.5 font-sans text-[10px] uppercase text-white">
              {letter}
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-sm font-semibold text-gray-900">{title}</h2>
              <p className="text-[11px] text-gray-500">
                {captured} of {fields.length} captured.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-8 w-8 flex-none place-items-center rounded-md text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>

        {/* Field list — full scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="divide-y divide-gray-100">
            {fields.map(f => {
              const sectionKey = `section_${f.section}` as keyof CompanyContext;
              const section = companyContext[sectionKey] as Record<string, unknown> | undefined;
              const value = section?.[f.key.split('.')[1]];
              return (
                <EditableField
                  key={f.key}
                  field={f}
                  value={value}
                  stateEntry={companyContext.field_states[f.key]}
                  onCommit={(v, action) => onCommit(f.key, v, action)}
                  autosave
                />
              );
            })}
          </div>
        </div>

      </aside>

      {/* Scrim */}
      {open && (
        <button
          type="button"
          aria-label="Close section drawer"
          onClick={onClose}
          className="fixed inset-0 z-[65] bg-black/20 backdrop-blur-[1px]"
        />
      )}
    </>
  );
};


const SectionFilterPill: React.FC<{
  letter: string | null;
  label: string;
  active: boolean;
  captured: number;
  total: number;
  onClick: () => void;
}> = ({ letter, label, active, captured, total, onClick }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={clsx(
      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors whitespace-nowrap',
      active
        ? 'border-gray-900 bg-gray-900 text-white'
        : 'border-gray-200 bg-white text-gray-600 hover:border-brand-500 hover:text-brand-700',
    )}
  >
    {letter && (
      <span className={clsx(
        'font-sans uppercase text-[10px]',
        active ? 'text-accent-500' : 'text-gray-400',
      )}>
        {letter}
      </span>
    )}
    <span>{label}</span>
    <span className={clsx(
      'tabular-nums text-[10px]',
      active ? 'text-white/70' : 'text-gray-400',
    )}>
      {captured}/{total}
    </span>
  </button>
);


// ── Editable dossier field row ───────────────────────────────────────
//
// Lifted to components/dossier/fieldKit.tsx so the onboarding card flow
// draws a field exactly as this page does. One renderer, several homes.

// ── Reusable primitives ───────────────────────────────────────────────

// Card primitive — Agents Anywhere signature surface.
// Per brand guide: cool off-white #eef3ff (brand-50) is the canonical
// card surface and "does a lot of work." Borders are hairline 1px ring
// on gray-200. NO left-border color-accent pattern (explicitly avoided
// in the brand kit). Primary cards lift with a soft brand-blue ring.
const Card: React.FC<{
  children: React.ReactNode;
  primary?: boolean;
}> = ({ children, primary }) => (
  <section className={clsx(
    'rounded-xl px-5 py-4 transition-colors',
    primary
      ? 'bg-brand-50 ring-1 ring-brand-500/20'
      : 'bg-brand-50 ring-1 ring-gray-200/60',
  )}>
    {children}
  </section>
);

const CardHead: React.FC<{ icon?: React.ReactNode; title: React.ReactNode }> = ({ icon, title }) => (
  <div className="mb-2 flex items-center gap-2 text-gray-700">
    {icon}
    <h3 className="text-sm font-semibold leading-snug text-gray-900">{title}</h3>
  </div>
);

// Primary CTA — brand-blue (#18191d) per the AA brand kit. Signature
// "blue glow" shadow on hover (rgba(24,25,29,0.25)), translateY(-1px).
const CardCTA: React.FC<{
  onClick?: () => void;
  href?: string;
  label: string;
  external?: boolean;
}> = ({ onClick, href, label, external }) => {
  const className =
    'mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-brand-600 hover:-translate-y-px hover:shadow-[0_4px_20px_rgba(24,25,29,0.25)]';
  if (href) {
    return (
      <a
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        className={className}
      >
        <span>{label}</span>
        {external ? <ExternalLink size={13} strokeWidth={2.25} /> : <ChevronRight size={14} strokeWidth={2.5} />}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      <span>{label}</span>
      {external ? <ExternalLink size={13} strokeWidth={2.25} /> : <ChevronRight size={14} strokeWidth={2.5} />}
    </button>
  );
};

const EmptyState: React.FC<{ icon: React.ReactNode; title: string; subtitle: string }> = ({ icon, title, subtitle }) => (
  <div className="grid place-items-center rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center">
    <div className="mb-2 text-gray-400">{icon}</div>
    <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
    <p className="mt-1 text-xs text-gray-500">{subtitle}</p>
  </div>
);

